import { useCallback, useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { ApprovalMode } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import { ApproverCandidateSchema } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import {
  DemandAutoConfirmMethod,
  LinkedPageKind,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Control } from 'react-hook-form'
import { useController, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { StageEditModal } from '~/components/approval/StageEditModal'
import type { StageLocal } from '~/components/approval/stage'
import { DsSelect } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'
import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import {
  isWorkflowAutomationAvailable,
  useWorkflowAutomationAvailability,
} from '~/hooks/useWorkflowAutomationAvailability'
import type { SubStepApprovalMode } from '~/pages/AdminWorkflowTemplates/models/approvalSetting'
import type { WorkflowTemplateFormValues } from '~/pages/AdminWorkflowTemplates/models/schema'

const MODE_LABEL_KEYS = {
  [ApprovalMode.NO_APPROVAL]:
    'page:adminWorkflowTemplates.subStep.approvalNoApproval',
  [ApprovalMode.APPROVAL]:
    'page:adminWorkflowTemplates.subStep.approvalRequired',
  [ApprovalMode.AUTO]: 'page:adminWorkflowTemplates.subStep.approvalAuto',
} as const satisfies Record<SubStepApprovalMode, string>

type Props = {
  /** リンク先で選べるモード (`allowedApprovalModes`)。 空配列では使わない。 */
  modes: readonly SubStepApprovalMode[]
  linkedPageKind: LinkedPageKind | undefined
  control: Control<WorkflowTemplateFormValues>
  bigStepIndex: number
  subIndex: number
  members: Member[]
  /** 保存時検証のエラー文言の要素 id。 エラーが無い時は undefined */
  errorId: string | undefined
  methodsOnly?: boolean
}

/**
 * サブステップの承認フロー設定 (spec AC-WFT-010e / 010f)。 承認モードの select と、
 * 要承認の時だけ承認段階モーダル (approval-flow §2 と同じ `StageEditModal`) を開くボタン。
 */
export const SubStepApprovalSettingField = ({
  modes,
  linkedPageKind,
  control,
  bigStepIndex,
  subIndex,
  members,
  errorId,
  methodsOnly = false,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const automationAvailability = useWorkflowAutomationAvailability()
  const automationAvailable = isWorkflowAutomationAvailable(
    automationAvailability,
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const path = `bigSteps.${bigStepIndex}.subSteps.${subIndex}` as const
  // 承認段階モーダルのタイトルに出すサブステップ名
  const subStepName = useWatch({ control, name: `${path}.name` })
  const {
    field: { value, onChange },
  } = useController({ control, name: `${path}.approvalSetting` })
  const method = useController({
    control,
    name: `${path}.demandAutoConfirmMethod`,
  })
  const otherMethod = useController({
    control,
    name: `${path}.demandAutoConfirmOtherMethod`,
  })
  const forecastMethodInputContext = useController({
    control,
    name: `${path}.forecastMethodInputContext`,
  })
  const section = useWatch({ control, name: 'section' })

  const candidates = useMemo(
    () =>
      members.map((m) =>
        create(ApproverCandidateSchema, {
          memberId: m.memberId,
          displayName: m.displayName,
        }),
      ),
    [members],
  )

  const handleStagesDone = useCallback(
    (stages: StageLocal[]) => {
      onChange({ mode: ApprovalMode.APPROVAL, stages })
      setIsModalOpen(false)
    },
    [onChange],
  )

  // 入札以外の form 値は必ず設定を持つ (schema が検証する)
  if (value === undefined) return null

  const name =
    subStepName !== ''
      ? subStepName
      : t('page:adminWorkflowTemplates.subStep.viewName')
  const autoUnavailable = !automationAvailable
  const hasAutoMode = modes.includes(ApprovalMode.AUTO)
  const catalog =
    automationAvailability.data?.demandAutoConfirmMethodOptions.find(
      (option) => option.section === section,
    )
  const toOptions = (
    choices: ReadonlyArray<{
      method: DemandAutoConfirmMethod
      selectable: boolean
    }>,
  ): Array<{
    value: string
    label: string
    disabled: boolean
    disabledReason?: string
  }> =>
    choices.map((choice) => ({
      value: String(choice.method),
      label: t(
        `page:adminWorkflowTemplates.subStep.forecastMethods.${choice.method}`,
      ),
      disabled: !choice.selectable,
      ...(choice.method === DemandAutoConfirmMethod.MONTHLY_PLAN_COPY
        ? {
            disabledReason: t(
              'page:adminWorkflowTemplates.subStep.approvalAutoUnavailable',
            ),
          }
        : {}),
    }))
  const modeOptions = [
    ...modes
      .filter(
        (m) =>
          !(
            m === ApprovalMode.AUTO &&
            linkedPageKind === LinkedPageKind.DEMAND_FORECAST_DAILY &&
            !automationAvailable &&
            value.mode !== ApprovalMode.AUTO
          ),
      )
      .map((m) => {
        const disabled = m === ApprovalMode.AUTO && autoUnavailable
        return {
          value: String(m),
          label: t(MODE_LABEL_KEYS[m]),
          ...(disabled
            ? {
                disabled: true,
                disabledReason: t(
                  'page:adminWorkflowTemplates.subStep.approvalAutoUnavailable',
                ),
              }
            : {}),
        }
      }),
    ...(!hasAutoMode &&
    linkedPageKind === LinkedPageKind.DEMAND_FORECAST_DAILY &&
    (automationAvailable || value.mode === ApprovalMode.AUTO)
      ? [
          {
            value: String(ApprovalMode.AUTO),
            label: t(MODE_LABEL_KEYS[ApprovalMode.AUTO]),
            disabled: true,
            disabledReason: t(
              'page:adminWorkflowTemplates.subStep.approvalAutoUnavailable',
            ),
          },
        ]
      : []),
  ]

  const methodId = `forecast-method-${bigStepIndex}-${subIndex}`
  const otherId = `forecast-other-method-${bigStepIndex}-${subIndex}`

  if (methodsOnly) {
    if (
      !automationAvailable ||
      linkedPageKind !== LinkedPageKind.DEMAND_FORECAST_DAILY ||
      value.mode !== ApprovalMode.AUTO ||
      catalog === undefined
    )
      return null
    return (
      <Box paddingLeft="32px">
        <>
          <FieldRoot
            width="full"
            maxWidth="560px"
            invalid={method.fieldState.invalid}
            required
          >
            <FieldLabel htmlFor={methodId}>
              {t('page:adminWorkflowTemplates.subStep.forecastMethod')}
              <Text as="span" color="Text.negativeHigh">
                *
              </Text>
            </FieldLabel>
            <DsSelect
              width="240px"
              aria-describedby={
                method.fieldState.invalid ? `${methodId}-error` : undefined
              }
              aria-label={t(
                'page:adminWorkflowTemplates.subStep.forecastMethod',
              )}
              inputId={methodId}
              onChange={(v) => {
                const next = enumSetValueAs(v) as
                  | DemandAutoConfirmMethod
                  | undefined
                method.field.onChange(next)
                // The main selection starts a new pair; retain the loaded context
                // separately so inherited fields still get backend cleanup.
                forecastMethodInputContext.field.onChange({
                  linkedPageKind,
                  approvalMode: value.mode,
                })
                // An explicit empty value avoids RHF restoring the loaded default.
                if (next !== DemandAutoConfirmMethod.CSV_REGISTERED_VALUES)
                  otherMethod.field.onChange(null)
              }}
              options={toOptions(catalog.methods)}
              value={method.field.value}
            />
            <FieldErrorText id={`${methodId}-error`}>
              {method.fieldState.error?.message}
            </FieldErrorText>
            <FieldHelperText color="Text.midEmphasis">
              {t('page:adminWorkflowTemplates.subStep.forecastNote')}
            </FieldHelperText>
          </FieldRoot>
          {method.field.value ===
            DemandAutoConfirmMethod.CSV_REGISTERED_VALUES && (
            <FieldRoot
              width="240px"
              invalid={otherMethod.fieldState.invalid}
              required
            >
              <FieldLabel htmlFor={otherId}>
                {t('page:adminWorkflowTemplates.subStep.otherForecastMethod')}
                <Text as="span" color="Text.negativeHigh">
                  *
                </Text>
              </FieldLabel>
              <DsSelect
                width="240px"
                aria-describedby={
                  otherMethod.fieldState.invalid
                    ? `${otherId}-error`
                    : undefined
                }
                aria-label={t(
                  'page:adminWorkflowTemplates.subStep.otherForecastMethod',
                )}
                inputId={otherId}
                onChange={(v) =>
                  otherMethod.field.onChange(
                    enumSetValueAs(v) as DemandAutoConfirmMethod | undefined,
                  )
                }
                options={toOptions(catalog.otherMethods)}
                value={otherMethod.field.value}
              />
              <FieldErrorText id={`${otherId}-error`}>
                {otherMethod.fieldState.error?.message}
              </FieldErrorText>
            </FieldRoot>
          )}
        </>
      </Box>
    )
  }

  return (
    <HStack align="center" flexShrink={0} gap={2}>
      <DsSelect
        width="153px"
        aria-label={t('page:adminWorkflowTemplates.subStep.approvalModeLabel')}
        invalid={errorId !== undefined}
        onChange={(v) => {
          const mode = enumSetValueAs(v) as ApprovalMode | undefined
          if (mode === undefined) return
          // 要承認以外へ切り替えたら段階を捨てる (design case-templates.html と同じ)。
          // 残すと保存時の段またぎ重複の検査に掛かり、見えない段階のせいで保存できない
          onChange({
            mode,
            stages: mode === ApprovalMode.APPROVAL ? value.stages : [],
          })
        }}
        options={modeOptions}
        value={value.mode}
      />
      {value.mode === ApprovalMode.APPROVAL && (
        <TertiaryButton
          aria-describedby={errorId}
          onClick={() => setIsModalOpen(true)}
          size="sm"
        >
          {value.stages.length > 0
            ? t('page:adminWorkflowTemplates.subStep.approvalStageCount', {
                count: value.stages.length,
              })
            : t('page:adminWorkflowTemplates.subStep.approvalStagesUnset')}
        </TertiaryButton>
      )}
      {isModalOpen && (
        <StageEditModal
          addStageLabel={t(
            'page:adminWorkflowTemplates.subStep.approvalStageModalAddStage',
          )}
          addStagePlacement="footer"
          candidates={candidates}
          confirmLabel={t('common:action.save')}
          initialStages={value.stages}
          isOpen
          onClose={() => setIsModalOpen(false)}
          onDone={handleStagesDone}
          subtitle={t(
            'page:adminWorkflowTemplates.subStep.approvalStageModalSubtitle',
            { name },
          )}
          title={t(
            'page:adminWorkflowTemplates.subStep.approvalStageModalTitle',
          )}
        />
      )}
    </HStack>
  )
}
